/**
 * useProductPage
 *
 * Fetches the product detail page bundle from GET /v1/storefront/pages/product/:slug.
 *
 * One request replaces 5+ separate fetches:
 *  - GET /api/v1/products/:slug              (product data)
 *  - GET /v1/pricing                          (default + variant overrides + tiers + discounts)
 *  - GET /api/v1/products/:uuid/reviews/stats
 *  - GET /api/v1/products/:uuid/recommendations
 *  - GET /api/v1/collections/menu             (nav)
 *  - GET /api/v1/collections/summer-sale      (header sale label)
 *
 * On success it seeds:
 *  - ['product', slug]                                            → useProduct(slug)
 *  - ['volume-tiers', productId, undefined, currency, country]    → useVolumeTiers
 *  - ['product-pricing', productId, currency, country]            → usePricing
 *  - ['recommendations', productId]                               → useRecommendedProducts
 *  - ['reviews', productId, 5, 0, undefined, true]                → ReviewsSection page 1
 *  - ['review-stats', productId]                                  → useReviewStats
 *  - module-level nav cache                                        → Header / CategoryNav
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Product,
  Review,
  ReviewStats as ClientReviewStats,
  CollectionDto,
  ProductPricing as ClientProductPricing,
} from '@template-project/storefront-bff-client';
import { createBffClient } from '@/lib/bff/create-storefront-client';
import { setCachedCollections, setSaleCachedCollection } from '@/lib/hooks/use-nav-collections';
import type { ProductEntity, PricingTier } from '@/domain/entities/product.entity';
import type { ReviewEntity, ReviewStats, ReviewsListResult } from '@/domain/entities/review.entity';
import type { CurrencyAmount } from '@/domain/value-objects/currency-amount.vo';

// ---------------------------------------------------------------------------
// Mappers (mirror product.bff-service.ts and review.bff-service.ts)
// ---------------------------------------------------------------------------

function mapProduct(p: Product): ProductEntity {
  return {
    id: p.id,
    uuid: p.id,
    slug: p.slug,
    title: p.title,
    description_html: p.description_html,
    tags: p.tags,
    images: p.images.map((img) => ({
      id: img.id,
      src: img.src,
      alt: img.alt,
      width: img.width,
      height: img.height,
      position: img.position,
      zoomUrl: img.zoomUrl,
    })),
    videos: p.videos?.map((v) => ({
      id: v.id,
      src: v.src,
      alt: v.alt,
      position: v.position,
    })),
    price: p.price as CurrencyAmount,
    compare_at_price: p.compare_at_price,
    is_out_of_stock: p.is_out_of_stock,
    variants: p.variants.map((v) => ({
      id: v.id,
      uuid: v.id,
      product_id: v.product_id,
      title: v.title,
      price: v.price as CurrencyAmount,
      compare_at_price: v.compare_at_price as CurrencyAmount | undefined,
      sku: v.sku,
      inventory_quantity: v.inventory_quantity,
      enabled: v.enabled,
      variation_attributes: v.variation_attributes,
    })),
    options: p.options,
    status: p.status,
    base_currency: p.base_currency,
    display_currency: p.display_currency,
  };
}

function mapReview(r: Review): ReviewEntity {
  return {
    id: r.id,
    productId: r.productId,
    customerId: r.customerId,
    customerName: r.customerName,
    rating: r.rating,
    title: r.title,
    content: r.content,
    verified: r.verified,
    helpful: r.helpful,
    notHelpful: r.notHelpful,
    customerImage: r.customerImage,
    productVariant: r.productVariant,
    createdAt: new Date(r.createdAt),
    updatedAt: new Date(r.updatedAt),
  };
}

function mapReviewStats(s: ClientReviewStats): ReviewStats {
  return {
    averageRating: s.averageRating,
    totalReviews: s.totalReviews,
    ratingDistribution: s.ratingDistribution as Record<number, number>,
  };
}

function toNavCollection(c: CollectionDto) {
  return { label: c.title, slug: c.slug, imageUri: c.imageUri };
}

export function mapClientPricingToTiers(p: ClientProductPricing | null): PricingTier[] {
  if (!p?.volume_tiers) return [];
  return p.volume_tiers.map((t) => ({
    min_quantity: t.min_quantity,
    max_quantity: t.max_quantity,
    unit_price: t.unit_price as CurrencyAmount,
    compare_at_price: t.compare_at_price as CurrencyAmount | undefined,
    label: t.label,
    savings_percent: t.savings_percent,
  }));
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface ProductPageData {
  product: ProductEntity;
  pricing: ClientProductPricing | null;
  recommendations: ProductEntity[];
  nav: {
    menuCollections: { label: string; slug: string; imageUri?: string }[];
    saleCollection: { slug: string; title: string } | null;
  };
}

export interface UseProductPageInput {
  slug: string;
  currency?: string;
  country?: string;
}

export function useProductPage(input: UseProductPageInput | string) {
  const arg: UseProductPageInput = typeof input === 'string' ? { slug: input } : input;
  const { slug, currency = 'USD', country } = arg;
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['product-page', slug, currency, country],
    queryFn: async (): Promise<ProductPageData> => {
      const client = createBffClient();
      const res = await client.getProductPage(slug, { currency, country });
      const product = mapProduct(res.data.product);

      queryClient.setQueryData(['product', slug], product);

      if (res.data.pricing) {
        queryClient.setQueryData(
          ['product-pricing', product.id, currency, country],
          res.data.pricing,
        );
        queryClient.setQueryData(
          ['volume-tiers', product.id, undefined, currency, country],
          mapClientPricingToTiers(res.data.pricing),
        );
      }

      const recommendations = (res.data.recommendations ?? []).map(mapProduct);
      queryClient.setQueryData(['recommendations', product.id], recommendations);

      if (res.data.reviews) {
        const mappedReviews: ReviewsListResult = {
          reviews: res.data.reviews.items.map(mapReview),
          total: res.data.reviews.total,
          limit: res.data.reviews.limit,
          offset: res.data.reviews.offset,
          hasMore: res.data.reviews.hasMore,
          stats: res.data.reviews.stats ? mapReviewStats(res.data.reviews.stats) : undefined,
        };
        queryClient.setQueryData(
          ['reviews', product.id, 5, 0, undefined, true],
          mappedReviews,
        );

        if (res.data.reviews.stats) {
          const stats = mapReviewStats(res.data.reviews.stats);
          queryClient.setQueryData(['review-stats', product.id], stats);
        }
      }

      if (res.data.nav.menuCollections.length > 0) {
        setCachedCollections(res.data.nav.menuCollections.map(toNavCollection));
      }
      setSaleCachedCollection(res.data.nav.saleCollection);

      return {
        product,
        pricing: res.data.pricing,
        recommendations,
        nav: {
          menuCollections: res.data.nav.menuCollections.map(toNavCollection),
          saleCollection: res.data.nav.saleCollection,
        },
      };
    },
    enabled: !!slug,
    staleTime: 60 * 1000,
    retry: 1,
  });
}
